import { RefObject } from "react";
import { useMotionValue, useScroll, useTransform, type MotionValue } from "framer-motion";
import { useIsMobile } from "./use-mobile";

interface TimelineRowMotion {
    y: MotionValue<number>;
    x: MotionValue<number>;
    scale: MotionValue<number>;
}

/**
 * Scroll-scrubbed motion values for a timeline row. The values are driven by
 * the row's own progress through the viewport — not a one-shot trigger — so
 * cards animate smoothly in both directions (down and back up) as the user
 * scrolls through the page.
 *
 * Curve:
 *   entering   (0.00 → 0.30) : slide in + scale up
 *   in view    (0.30 → 0.70) : settled, scale 1
 *   leaving    (0.70 → 1.00) : gentle drift up + slight scale-down
 *
 * `isEven` mirrors the horizontal slide so the card flies in from its own
 * column rather than always the same side.
 *
 * NOTE: `opacity` is intentionally NOT animated. Chromium & WebKit skip
 * painting `backdrop-filter` on descendants whenever any ancestor has
 * opacity < 1, which broke the frosted-glass blur on `.glass-v2` cards inside
 * the animated row. Transforms (y, x, scale) don't trigger that quirk because
 * the cards already use `transform: translateZ(0)` + `isolation: isolate` to
 * force their own compositing layer.
 */
export const useTimelineRowMotion = (
    ref: RefObject<HTMLElement>,
    isEven: boolean,
): TimelineRowMotion => {
    const isMobile = useIsMobile();

    // Hooks always run (React rules) — but on mobile we return static
    // motion values instead of the scroll-linked ones, so `style={{y,x,scale}}`
    // on the consumer stays constant and the compositor doesn't repaint each
    // row on every scroll tick. With 5 timelines × 4-6 rows each, writing
    // transforms per tick is what was dropping frames on Adreno/Mali mid-tier.
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    const y = useTransform(
        scrollYProgress,
        [0, 0.25, 0.7, 1],
        [70, 0, -8, -40],
    );
    const x = useTransform(
        scrollYProgress,
        [0, 0.3, 0.7, 1],
        [isEven ? -50 : 50, 0, 0, isEven ? 10 : -10],
    );
    const scale = useTransform(
        scrollYProgress,
        [0, 0.3, 0.7, 1],
        [0.9, 1, 1, 0.95],
    );

    const staticY = useMotionValue(0);
    const staticX = useMotionValue(0);
    const staticScale = useMotionValue(1);

    if (isMobile) return { y: staticY, x: staticX, scale: staticScale };
    return { y, x, scale };
};
